import { createFileRoute, Link, useRouter } from "@tanstack/react-router"
import { Pencil } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import type { MemberRosterState } from "@/core/activity-points/types"
import type {
  GuildRankTitle,
  GuildSpecialTitle,
} from "@/core/guild-members/titles"

import { ActivityPointsCard } from "@/components/members/activity-points-card"
import {
  currentMonth,
  formatPeriod,
  MONTH_NAMES,
  MonthPicker,
  parsePeriod,
} from "@/components/members/month-picker"
import { StatusAlert } from "@/components/shared/status-alert"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { toast } from "@/components/ui/toast"
import {
  GUILD_RANK_CATALOG,
  GUILD_SPECIAL_TITLE_CATALOG,
} from "@/core/guild-members/titles"
import {
  useActivityPointsMonthQuery,
  useSaveActivityPointsMonthMutation,
} from "@/hooks/activity-points"
import {
  useAdminMemberProfileQuery,
  useDeactivateMemberMutation,
  useSetMemberJoinDateMutation,
} from "@/hooks/admin-member-profile"
import { useUpdateMemberTitlesMutation } from "@/hooks/member-titles"
import { AdminMemberProfileApiError } from "@/lib/admin-member-profile-api"
import { formatGuildDateTime } from "@/lib/guild-date-format"
import {
  formatGuildJoinDate,
  guildJoinCalendarDate,
  guildJoinTenureDays,
  parseGuildJoinCalendarDate,
} from "@/lib/guild-join-date"
import { memberDisplayLabel } from "@/lib/member-display-label"
import {
  memberPeriodKey,
  useMemberPointsState,
  useMembersFocusIntent,
  useMembersRouteStore,
  useMemberTitleState,
} from "@/lib/stores/members-ui-store"

const MAX_INTEGER = 2_147_483_647
const POINTS_ERROR_ID = "member-points-error"

export const Route = createFileRoute("/_authed/_admin/members/$memberId")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { month: string | undefined; state: MemberRosterState } => ({
    month: typeof search.month === "string" ? search.month : undefined,
    state:
      search.state === "departed" ||
      search.state === "all" ||
      search.state === "present" ||
      search.state === "unknown"
        ? search.state
        : "active",
  }),
  component: MemberDetailPage,
})

function MemberDetailPage() {
  const { memberId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const { auth } = Route.useRouteContext()
  const router = useRouter()
  const parsed = parsePeriod(search.month)
  const selectedPeriod = parsed?.period ?? currentMonth()
  const { year, month } = parsePeriod(selectedPeriod)!
  const profileQuery = useAdminMemberProfileQuery(memberId)
  const monthQuery = useActivityPointsMonthQuery(year, month, "all")
  const savePoints = useSaveActivityPointsMonthMutation()
  const updateTitles = useUpdateMemberTitlesMutation(auth.user?.userId ?? null)
  const membersStore = useMembersRouteStore()
  const { actions } = membersStore
  const titleErrorRef = useRef<HTMLDivElement>(null)
  const selectedTarget = memberPeriodKey(memberId, selectedPeriod)
  const pointsState = useMemberPointsState(membersStore, selectedTarget)
  const titleState = useMemberTitleState(membersStore, memberId)
  const focusIntent = useMembersFocusIntent(membersStore)
  const pointsInputRef = useRef<HTMLInputElement>(null)
  const editPointsRef = useRef<HTMLButtonElement>(null)
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const deactivateMember = useDeactivateMemberMutation(memberId)
  const [deactivateOpen, setDeactivateOpen] = useState(false)
  const [deactivationNote, setDeactivationNote] = useState("")
  const [deactivationError, setDeactivationError] = useState<string | null>(
    null,
  )
  const deactivationErrorRef = useRef<HTMLDivElement>(null)
  const membershipHeadingRef = useRef<HTMLDivElement>(null)
  const setJoinDate = useSetMemberJoinDateMutation()
  const [joinEditor, setJoinEditor] = useState<{
    memberId: string
    draft: string
    error: string | null
  } | null>(null)
  const joinErrorRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    setJoinEditor((current) =>
      current?.memberId === memberId ? current : null,
    )
  }, [memberId])

  useEffect(() => {
    if (search.month !== selectedPeriod)
      void navigate({
        search: { month: selectedPeriod, state: search.state },
        replace: true,
      })
  }, [navigate, search.month, search.state, selectedPeriod])

  useEffect(() => {
    actions.closeTitles(memberId)
  }, [actions, memberId])

  useEffect(() => {
    if (deactivateOpen && deactivationError && !deactivateMember.isPending) {
      deactivationErrorRef.current?.focus()
    }
  }, [deactivateMember.isPending, deactivateOpen, deactivationError])

  useEffect(() => {
    if (
      joinEditor?.memberId === memberId &&
      joinEditor.error &&
      !setJoinDate.isPending
    )
      joinErrorRef.current?.focus()
  }, [joinEditor, memberId, setJoinDate.isPending])

  useEffect(() => {
    if (
      !focusIntent ||
      focusIntent.key !==
        (focusIntent.area === "points" ? selectedTarget : memberId)
    )
      return
    const target =
      focusIntent.area === "points"
        ? focusIntent.target === "edit"
          ? editPointsRef.current
          : pointsInputRef.current
        : focusIntent.area === "titles"
          ? titleErrorRef.current
          : null
    if (target && !("disabled" in target && target.disabled)) target.focus()
    actions.consumeFocusIntent(focusIntent)
  }, [
    actions,
    focusIntent,
    memberId,
    pointsState.error,
    pointsState.mode,
    pointsState.pending,
    selectedTarget,
    titleState.error,
  ])

  const profile = profileQuery.data
  const label = memberDisplayLabel({
    displayName: profile?.displayName,
    memberId,
  })
  const initials = label
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("")
  const rankLabel = GUILD_RANK_CATALOG.find(
    ({ value }) => value === profile?.rankTitle,
  )?.label
  const specialLabels = GUILD_SPECIAL_TITLE_CATALOG.filter(({ value }) =>
    profile?.specialTitles.includes(value),
  ).map(({ label }) => label)
  const joinedAt = profile?.joinedAt ? new Date(profile.joinedAt) : null
  const validJoinDate = joinedAt && !Number.isNaN(joinedAt.getTime())
  const matchingMonth =
    monthQuery.data?.year === year && monthQuery.data.month === month
      ? monthQuery.data
      : null
  const rosterRow = matchingMonth?.rows.find((row) => row.memberId === memberId)
  const { draft: titleDraft, error: titleError, open: titleOpen } = titleState

  const activeJoinEditor = joinEditor?.memberId === memberId ? joinEditor : null
  const joinDate = validJoinDate ? guildJoinCalendarDate(joinedAt) : null
  const joinPending =
    setJoinDate.isPending && setJoinDate.variables?.memberId === memberId
  const submitJoinDate = async (joinedDate: string | null) => {
    if (!activeJoinEditor || joinPending) return
    if (joinedDate === joinDate) {
      setJoinEditor(null)
      return
    }
    if (joinedDate && !parseGuildJoinCalendarDate(joinedDate)) {
      setJoinEditor({
        ...activeJoinEditor,
        error: "Enter a valid calendar date.",
      })
      return
    }
    const submittedMemberId = memberId
    const submittedEditor = { ...activeJoinEditor, error: null }
    setJoinEditor(submittedEditor)
    try {
      await setJoinDate.mutateAsync({ memberId: submittedMemberId, joinedDate })
      setJoinEditor((current) => (current === submittedEditor ? null : current))
    } catch (error) {
      setJoinEditor((current) =>
        current === submittedEditor
          ? {
              ...current,
              error:
                error instanceof AdminMemberProfileApiError
                  ? error.message
                  : "Guild join date was not saved.",
            }
          : current,
      )
    }
  }
  const openTitles = (open: boolean) => {
    if (!open && updateTitles.isPending) return
    if (open && profile)
      actions.openTitles(memberId, {
        rankTitle: profile.rankTitle,
        specialTitles: [...profile.specialTitles],
      })
    else actions.closeTitles(memberId)
  }

  const submitTitles = async () => {
    if (!titleDraft) return
    const submittedMemberId = memberId
    actions.beginTitleSave(submittedMemberId)
    try {
      await updateTitles.mutateAsync({
        memberId: submittedMemberId,
        ...titleDraft,
      })
      actions.completeTitleSave(submittedMemberId)
    } catch (error) {
      actions.failTitleSave(
        submittedMemberId,
        error instanceof Error ? error.message : "Titles were not saved.",
      )
    }
  }
  const submitDeactivation = async () => {
    if (deactivateMember.isPending) return
    const note = deactivationNote.trim()
    if (!note || note.length > 500) {
      setDeactivationError(
        note ? "Note must be 500 characters or fewer." : "Enter a note.",
      )
      return
    }
    setDeactivationError(null)
    try {
      await deactivateMember.mutateAsync(note)
      setDeactivateOpen(false)
      setDeactivationNote("")
      queueMicrotask(() => membershipHeadingRef.current?.focus())
    } catch (error) {
      setDeactivationError(
        error instanceof AdminMemberProfileApiError
          ? error.message
          : "Membership was not deactivated.",
      )
    }
  }

  const editPoints = () => {
    if (!rosterRow) return
    actions.editPoints(selectedTarget, rosterRow.points)
  }
  const cancelPoints = () => {
    actions.cancelPoints(selectedTarget)
  }
  const submitPoints = async () => {
    if (!rosterRow || pointsState.pending) return
    const points = Number(pointsState.draft)
    const blankMessage =
      rosterRow.points === null
        ? "Enter points before saving."
        : "Points cannot be cleared here; enter a value or cancel."
    if (
      pointsState.draft === "" ||
      !Number.isInteger(points) ||
      points < 0 ||
      points > MAX_INTEGER
    ) {
      actions.rejectPoints(
        selectedTarget,
        pointsState,
        pointsState.draft === ""
          ? blankMessage
          : "Points must be a whole number between 0 and 2147483647.",
      )
      return
    }
    const submittedMemberId = memberId
    const submittedTarget = selectedTarget
    actions.beginPointsSave(submittedTarget, pointsState)
    try {
      await savePoints.mutateAsync({
        year,
        month,
        state: "all",
        entries: [{ memberId: submittedMemberId, points }],
      })
      actions.completePointsSave(submittedTarget)
      if (
        mountedRef.current &&
        router.state.location.pathname ===
          `/members/${encodeURIComponent(submittedMemberId)}` &&
        memberPeriodKey(
          submittedMemberId,
          parsePeriod(router.state.location.search.month)?.period ??
            currentMonth(),
        ) === submittedTarget
      ) {
        toast.success("Activity points saved.")
      }
    } catch {
      actions.failPointsSave(submittedTarget, pointsState)
    }
  }

  const notFound =
    profileQuery.error instanceof AdminMemberProfileApiError &&
    profileQuery.error.status === 404 &&
    profileQuery.error.code === "MEMBER_NOT_FOUND"
  if (profileQuery.isLoading)
    return <StatusAlert tone="info">Loading member profile...</StatusAlert>
  if (notFound)
    return <StatusAlert tone="warning">Member profile not found.</StatusAlert>
  if (profileQuery.isError || !profile)
    return (
      <StatusAlert tone="danger">
        <div className="flex items-center justify-between gap-3">
          <span>Unable to load member profile.</span>
          <Button variant="outline" onClick={() => void profileQuery.refetch()}>
            Retry
          </Button>
        </div>
      </StatusAlert>
    )

  return (
    <div className="grid gap-4 md:gap-5">
      <Link
        to="/members"
        search={{ month: selectedPeriod, state: search.state }}
        className="w-fit text-sm font-medium underline underline-offset-4"
      >
        Back to members
      </Link>
      <Card size="lg" className="border-primary/10">
        <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
          <Avatar className="size-24 bg-primary/10 text-primary sm:size-32">
            {profile.avatarUrl ? (
              <AvatarImage src={profile.avatarUrl} alt={label} />
            ) : null}
            <AvatarFallback className="text-3xl font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1 space-y-3">
            <h1 className="text-3xl font-semibold tracking-tight break-words sm:text-4xl">
              {label}
            </h1>
            <div className="space-y-1 text-sm text-muted-foreground">
              <p>
                {validJoinDate
                  ? `Member for ${guildJoinTenureDays(joinedAt)} days`
                  : "Membership tenure unavailable"}
              </p>
              <div className="flex items-center gap-1">
                <p>
                  {validJoinDate
                    ? `Joined ${formatGuildJoinDate(joinedAt)}`
                    : "Join date unavailable"}
                </p>
                {!activeJoinEditor ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Edit join date"
                    disabled={joinPending}
                    onClick={() =>
                      setJoinEditor({
                        memberId,
                        draft: joinDate ?? "",
                        error: null,
                      })
                    }
                  >
                    <Pencil aria-hidden="true" />
                  </Button>
                ) : null}
              </div>
            </div>
            {activeJoinEditor ? (
              <div className="grid max-w-sm gap-2">
                <Label htmlFor="member-join-date">Guild join date</Label>
                <Input
                  id="member-join-date"
                  type="date"
                  disabled={joinPending}
                  value={activeJoinEditor.draft}
                  aria-invalid={activeJoinEditor.error ? true : undefined}
                  aria-describedby={
                    activeJoinEditor.error
                      ? "member-join-date-error"
                      : undefined
                  }
                  onChange={(event) =>
                    setJoinEditor({
                      ...activeJoinEditor,
                      draft: event.target.value,
                      error: null,
                    })
                  }
                />
                {activeJoinEditor.error ? (
                  <div
                    id="member-join-date-error"
                    ref={joinErrorRef}
                    tabIndex={-1}
                    role="alert"
                  >
                    <StatusAlert tone="danger">
                      {activeJoinEditor.error}
                    </StatusAlert>
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    disabled={
                      joinPending ||
                      !activeJoinEditor.draft ||
                      activeJoinEditor.draft === joinDate
                    }
                    onClick={() => void submitJoinDate(activeJoinEditor.draft)}
                  >
                    {joinPending ? "Saving..." : "Save"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={joinPending || joinDate === null}
                    onClick={() => void submitJoinDate(null)}
                  >
                    Clear to unknown
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={joinPending}
                    onClick={() => setJoinEditor(null)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
              {rankLabel ? (
                <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
                  {rankLabel}
                </span>
              ) : null}
              {specialLabels.map((title) => (
                <span
                  key={title}
                  className="rounded-full bg-muted px-3 py-1 text-sm"
                >
                  {title}
                </span>
              ))}
              {!rankLabel && specialLabels.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No guild titles assigned
                </p>
              ) : null}
              <Dialog open={titleOpen} onOpenChange={openTitles}>
                <DialogTrigger
                  render={
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Edit guild titles"
                    />
                  }
                >
                  <Pencil aria-hidden="true" />
                </DialogTrigger>
                <DialogContent showCloseButton={!updateTitles.isPending}>
                  <DialogHeader>
                    <DialogTitle>Edit titles for {label}</DialogTitle>
                    <DialogDescription>
                      Choose this member's guild rank and special titles.
                    </DialogDescription>
                  </DialogHeader>
                  {titleDraft ? (
                    <div className="grid gap-4">
                      <div className="grid gap-1.5">
                        <Label htmlFor="member-rank-title">Rank</Label>
                        <Select
                          disabled={updateTitles.isPending}
                          value={titleDraft.rankTitle ?? "none"}
                          onValueChange={(value) =>
                            actions.updateTitleDraft(memberId, {
                              ...titleDraft,
                              rankTitle:
                                value === "none"
                                  ? null
                                  : (value as GuildRankTitle),
                            })
                          }
                        >
                          <SelectTrigger id="member-rank-title">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No rank</SelectItem>
                            {GUILD_RANK_CATALOG.map((rank) => (
                              <SelectItem key={rank.value} value={rank.value}>
                                {rank.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <fieldset
                        className="grid gap-3"
                        disabled={updateTitles.isPending}
                      >
                        <legend className="text-sm font-medium">
                          Special titles
                        </legend>
                        {GUILD_SPECIAL_TITLE_CATALOG.map((title) => {
                          const checked = titleDraft.specialTitles.includes(
                            title.value,
                          )
                          return (
                            <div
                              key={title.value}
                              className="flex items-center gap-2"
                            >
                              <Checkbox
                                id={`profile-title-${title.value}`}
                                checked={checked}
                                onCheckedChange={(next) =>
                                  actions.updateTitleDraft(memberId, {
                                    ...titleDraft,
                                    specialTitles: next
                                      ? [
                                          ...titleDraft.specialTitles,
                                          title.value,
                                        ]
                                      : titleDraft.specialTitles.filter(
                                          (value) => value !== title.value,
                                        ),
                                  })
                                }
                              />
                              <Label htmlFor={`profile-title-${title.value}`}>
                                {title.label}
                              </Label>
                            </div>
                          )
                        })}
                      </fieldset>
                    </div>
                  ) : null}
                  {titleError ? (
                    <div ref={titleErrorRef} tabIndex={-1} role="alert">
                      <StatusAlert tone="danger">
                        Failed to save titles for {label}: {titleError}
                      </StatusAlert>
                    </div>
                  ) : null}
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={updateTitles.isPending}
                      onClick={() => openTitles(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      disabled={updateTitles.isPending || !titleDraft}
                      onClick={() => void submitTitles()}
                    >
                      {updateTitles.isPending ? "Saving..." : "Save titles"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle
            ref={membershipHeadingRef}
            role="heading"
            aria-level={2}
            tabIndex={-1}
          >
            Membership status
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 text-sm md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
          <div className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
            <p>
              <span className="font-medium">Status:</span>{" "}
              {profile.lifecycle.status === "active"
                ? "Active"
                : profile.lifecycle.status === "inactive"
                  ? "Inactive"
                  : "Pending"}
            </p>
            <p>
              <span className="font-medium">Discord presence:</span>{" "}
              {profile.lifecycle.discordPresence === "present"
                ? "Present in Discord"
                : profile.lifecycle.discordPresence === "departed"
                  ? "Left or was removed from Discord"
                  : "Discord presence unknown"}
            </p>
            {profile.lifecycle.status === "inactive" ? (
              <p>
                <span className="font-medium">Inactive since:</span>{" "}
                {profile.lifecycle.deactivatedAt
                  ? formatGuildDateTime(profile.lifecycle.deactivatedAt)
                  : "unknown"}
              </p>
            ) : null}
            {profile.lifecycle.deactivationNote ? (
              <p className="min-w-0 break-words whitespace-pre-wrap">
                <span className="font-medium">Note:</span>{" "}
                {profile.lifecycle.deactivationNote}
              </p>
            ) : null}
            {profile.lifecycle.discordPresenceChangedAt ? (
              <p>
                <span className="font-medium">Presence last changed:</span>{" "}
                {formatGuildDateTime(
                  profile.lifecycle.discordPresenceChangedAt,
                )}
              </p>
            ) : null}
          </div>
          {profile.lifecycle.status === "active" ? (
            <Dialog
              open={deactivateOpen}
              onOpenChange={(open) => {
                if (deactivateMember.isPending) return
                setDeactivateOpen(open)
                if (open) setDeactivationError(null)
              }}
            >
              <DialogTrigger
                render={
                  <Button
                    type="button"
                    variant="destructive"
                    className="w-fit md:justify-self-end"
                  />
                }
              >
                Deactivate membership
              </DialogTrigger>
              <DialogContent showCloseButton={!deactivateMember.isPending}>
                <DialogHeader>
                  <DialogTitle>Deactivate membership for {label}</DialogTitle>
                  <DialogDescription>
                    This removes only the guild-member role. The person will
                    remain in Discord.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-1.5">
                  <Label htmlFor="deactivation-note">Administrative note</Label>
                  <Textarea
                    id="deactivation-note"
                    className="resize-none"
                    required
                    maxLength={500}
                    disabled={deactivateMember.isPending}
                    value={deactivationNote}
                    aria-invalid={deactivationError ? true : undefined}
                    aria-describedby={
                      deactivationError
                        ? "deactivation-error deactivation-count"
                        : "deactivation-count"
                    }
                    onChange={(event) => {
                      setDeactivationNote(event.target.value)
                      setDeactivationError(null)
                    }}
                  />
                  <p
                    id="deactivation-count"
                    className="text-xs text-muted-foreground"
                  >
                    {deactivationNote.length}/500 characters
                  </p>
                </div>
                {deactivationError ? (
                  <div
                    id="deactivation-error"
                    ref={deactivationErrorRef}
                    tabIndex={-1}
                  >
                    <StatusAlert tone="danger">{deactivationError}</StatusAlert>
                  </div>
                ) : null}
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={deactivateMember.isPending}
                    onClick={() => setDeactivateOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={deactivateMember.isPending}
                    onClick={() => void submitDeactivation()}
                  >
                    {deactivateMember.isPending
                      ? "Deactivating..."
                      : "Deactivate membership"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Selected month points</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-[minmax(16rem,20rem)_minmax(0,1fr)] md:items-end">
          <MonthPicker
            year={year}
            month={month}
            disabled={pointsState.mode === "editing" || pointsState.pending}
            onSelect={(period) =>
              void navigate({ search: { month: period, state: search.state } })
            }
          />
          {!matchingMonth ? (
            monthQuery.isError ? (
              <StatusAlert tone="danger">
                Unable to load activity points.
              </StatusAlert>
            ) : (
              <StatusAlert tone="info">Loading activity points...</StatusAlert>
            )
          ) : (
            <>
              {monthQuery.isError ? (
                <StatusAlert tone="danger">
                  Unable to refresh activity points. Showing saved data.
                </StatusAlert>
              ) : null}
              {!rosterRow ? (
                <StatusAlert tone="info">
                  Points unavailable for this member in {MONTH_NAMES[month - 1]}{" "}
                  {year}
                </StatusAlert>
              ) : pointsState.mode === "editing" ? (
                <div className="grid gap-3">
                  <Label htmlFor="member-points">Points for {label}</Label>
                  <Input
                    ref={pointsInputRef}
                    id="member-points"
                    type="number"
                    min={0}
                    max={MAX_INTEGER}
                    step={1}
                    disabled={pointsState.pending}
                    value={pointsState.draft}
                    aria-invalid={pointsState.error ? true : undefined}
                    aria-describedby={
                      pointsState.error ? POINTS_ERROR_ID : undefined
                    }
                    onChange={(event) =>
                      actions.updatePointsDraft(
                        selectedTarget,
                        event.target.value,
                      )
                    }
                  />
                  {pointsState.error ? (
                    <StatusAlert tone="danger">
                      <span id={POINTS_ERROR_ID}>{pointsState.error}</span>
                    </StatusAlert>
                  ) : null}
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={pointsState.pending}
                      onClick={cancelPoints}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      disabled={
                        pointsState.pending ||
                        (rosterRow.points === null && pointsState.draft === "")
                      }
                      onClick={() => void submitPoints()}
                    >
                      {pointsState.pending ? "Saving..." : "Save points"}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
                  <p className="md:justify-self-center">
                    {rosterRow.points === null
                      ? "Not entered"
                      : rosterRow.points}
                  </p>
                  <Button
                    ref={editPointsRef}
                    type="button"
                    className="w-fit md:justify-self-end"
                    onClick={editPoints}
                  >
                    Edit points
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      <ActivityPointsCard
        activityPoints={profile.activityPoints}
        isLoading={profileQuery.isLoading}
        isError={profileQuery.isError}
      />
    </div>
  )
}
